import React, { useState } from 'react';
import { Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DropzoneArea from '../DropzoneArea';

const ExcelJournalImportModal = ({
    open,
    onClose,
    previewExcelJournal,
    commitExcelJournal,
    direction,
    t
}) => {
    const [excelJournalFile, setExcelJournalFile] = useState(null);
    const [excelJournalOpeningBalanceEntries, setExcelJournalOpeningBalanceEntries] = useState([]);
    const [excelJournalPreviewDone, setExcelJournalPreviewDone] = useState(false);
    const [excelJournalProcessing, setExcelJournalProcessing] = useState(false);
    const [excelJournalParsedRows, setExcelJournalParsedRows] = useState(0);
    const handleExcelJournalDrop = files => {
        if (files?.length) setExcelJournalFile(files[0]);
    };

    const handleExcelJournalPreview = async () => {
        if (!excelJournalFile) return;

        try {
            setExcelJournalProcessing(true);
            const res = await previewExcelJournal(excelJournalFile);
            const openingBalanceEntries = res?.openingBalanceEntries ?? [];
            const parsedRows = res?.parsedRows ?? 0;

            setExcelJournalOpeningBalanceEntries(openingBalanceEntries);
            setExcelJournalParsedRows(parsedRows);
            setExcelJournalPreviewDone(true);
        } catch (err) {
            console.error('Excel journal preview failed:', err);
            setExcelJournalPreviewDone(false);
        } finally {
            setExcelJournalProcessing(false);
        }
    };

    const handleExcelJournalCommit = async () => {
        if (!excelJournalFile) return;

        try {
            setExcelJournalProcessing(true);

            // Import journal entries
            const res = await commitExcelJournal(excelJournalFile, null);
            
            if (res && res.errorMessages && res.errorMessages.length > 0) {
                console.error('Import errors:', res.errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Excel journal commit error:', err);
        } finally {
            setExcelJournalProcessing(false);
        }
    };

    const handleClose = () => {
        setExcelJournalFile(null);
        setExcelJournalPreviewDone(false);
        setExcelJournalOpeningBalanceEntries([]);
        setExcelJournalParsedRows(0);
        onClose();
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('he-IL');
        } catch {
            return dateString;
        }
    };

    const formatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        }).format(amount);
    };

    const formatSortCode = (sortCode) => {
        if (!sortCode) return '';
        if (sortCode === '1') return 'חובה';
        if (sortCode === '2') return 'זכות';
        return sortCode;
    };

    // Calculate totals
    const calculateTotals = () => {
        let totalDebit = 0;
        let totalCredit = 0;

        excelJournalOpeningBalanceEntries.forEach(entry => {
            const amount = parseFloat(entry.amount) || 0;
            if (entry.sortCode === '1') {
                totalDebit += amount;
            } else if (entry.sortCode === '2') {
                totalCredit -= amount; // Make credit negative
            }
        });

        return {
            totalDebit,
            totalCredit,
            total: totalDebit + totalCredit // Now totalCredit is already negative
        };
    };

    const totals = excelJournalPreviewDone ? calculateTotals() : { totalDebit: 0, totalCredit: 0, total: 0 };

    if (!open) return null;

    return (
        <div className="download-modal-overlay" onClick={handleClose}>
            <div className="download-modal" onClick={e => e.stopPropagation()}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: excelJournalPreviewDone ? '1200px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">
                            Excel - יתרת פתיחה חשבשבת
                        </h4>
                        <IconButton 
                            className="client-back-icon" 
                            onClick={handleClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div>
                            <h5>{t('UploadFile')} (.xlsx/.xls)</h5>
                            <DropzoneArea
                                key={excelJournalFile ? excelJournalFile.name : 'excel-journal-empty'}
                                onDrop={handleExcelJournalDrop}
                                accept=".xls,.xlsx"
                                filesLimit={1}
                            />
                            {excelJournalFile && (
                                <div style={{ marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {excelJournalFile.name}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button
                            variant="contained"
                            onClick={handleExcelJournalPreview}
                            disabled={!excelJournalFile || excelJournalProcessing}
                        >
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!excelJournalPreviewDone || excelJournalProcessing}
                            onClick={handleExcelJournalCommit}
                        >
                            {excelJournalProcessing ? t('Processing') : t('Import')}
                        </Button>
                    </div>

                    {excelJournalPreviewDone && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <div style={{ marginBottom: 15 }}>
                                <p style={{ fontSize: '14px', color: '#666' }}>
                                    {t('ParsedRows') || 'שורות שנקראו'}: <strong>{excelJournalParsedRows}</strong> | 
                                    {' '}{t('OpeningBalanceEntries') || 'יתרות פתיחה'}: <strong>{excelJournalOpeningBalanceEntries.length}</strong>
                                </p>
                            </div>

                            {/* Summary Section */}
                            {excelJournalOpeningBalanceEntries.length > 0 && (
                                <div style={{ 
                                    marginBottom: 20, 
                                    padding: '15px', 
                                    backgroundColor: '#f5f5f5', 
                                    borderRadius: '8px',
                                    border: '1px solid #ddd'
                                }}>
                                    <h5 style={{ marginTop: 0, marginBottom: 15 }}>{t('Summary') || 'סיכום'}</h5>
                                    <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
                                        <div style={{ flex: 1, minWidth: '150px' }}>
                                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                {t('TotalDebit') || 'סה"כ חובה'}
                                            </div>
                                            <div style={{ 
                                                fontSize: '18px', 
                                                fontWeight: 'bold', 
                                                color: totals.totalDebit < 0 ? '#d32f2f' : totals.totalDebit > 0 ? '#388e3c' : '#666'
                                            }}>
                                                ₪{formatAmount(totals.totalDebit)}
                                            </div>
                                        </div>
                                        <div style={{ flex: 1, minWidth: '150px' }}>
                                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                {t('TotalCredit') || 'סה"כ זכות'}
                                            </div>
                                            <div style={{ 
                                                fontSize: '18px', 
                                                fontWeight: 'bold', 
                                                color: totals.totalCredit < 0 ? '#d32f2f' : totals.totalCredit > 0 ? '#388e3c' : '#666'
                                            }}>
                                                ₪{formatAmount(totals.totalCredit)}
                                            </div>
                                        </div>
                                        <div style={{ flex: 1, minWidth: '150px' }}>
                                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                {t('Total') || 'סה"כ כולל'}
                                            </div>
                                            <div style={{ 
                                                fontSize: '18px', 
                                                fontWeight: 'bold', 
                                                color: totals.total < 0 ? '#d32f2f' : totals.total > 0 ? '#388e3c' : '#666'
                                            }}>
                                                ₪{formatAmount(totals.total)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {excelJournalOpeningBalanceEntries.length === 0 ? (
                                <p style={{ color: '#666' }}>{t('NoOpeningBalanceEntries') || 'לא נמצאו יתרות פתיחה'}</p>
                            ) : (
                                <>
                                    <h5>{t('OpeningBalanceEntries') || 'יתרות פתיחה'}</h5>
                                    <table className="sortcode-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('CardId') || 'מזהה כרטיס'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('CardName') || 'שם הכרטיס'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('SortCode') || 'קוד מיון'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('ValueDate') || 'תאריך רישום'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('Reference') || 'אסמכתא'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('Description') || 'תיאור'}</th>
                                                <th style={{ padding: '10px', textAlign: 'right', border: '1px solid #ddd' }}>{t('Amount') || 'סכום'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {excelJournalOpeningBalanceEntries.map((entry, i) => {
                                                const isCredit = entry.sortCode === '2';
                                                const displayAmount = isCredit ? -(parseFloat(entry.amount) || 0) : (parseFloat(entry.amount) || 0);
                                                
                                                return (
                                                    <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.card1Id || ''}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.card1Name || ''}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{formatSortCode(entry.sortCode)}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{formatDate(entry.valueDate)}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.reference || ''}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.description || ''}</td>
                                                        <td style={{ 
                                                            padding: '10px', 
                                                            border: '1px solid #ddd', 
                                                            textAlign: 'right',
                                                            color: isCredit ? '#d32f2f' : 'inherit',
                                                            fontWeight: isCredit ? 'bold' : 'normal'
                                                        }}>
                                                            {formatAmount(displayAmount)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ExcelJournalImportModal;
